import React, { memo, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Logger } from '@saviynt/common';
import {
  Box,
  Button,
  Icon,
  Link as DsLink,
  Loader,
  OverlayProvider,
  Typography,
} from '@saviynt/design-system';

import IntegrationPageError from '../../components/IntegrationPageError/IntegrationPageError';
import config from '../../config/obNextGenConfig';
import ApplicationCard from '../../features/DiscoverCMDBApps/components/ApplicationCard/ApplicationCard';
import DiscoverCMDBFooter from '../../features/DiscoverCMDBApps/components/DiscoverCMDBFooter/DiscoverCMDBFooter';
import { useGetDiscoveryApplicationsQuery } from '../../features/DiscoverCMDBApps/data-layer/ApplicationSlice';
import useLocalization from '../../hooks/use-localize';

import './discovery.css';

/**
 * Discovery Component
 *
 * Renders the Ob Next Gen Landing page with application discovery features.
 * Displays a list of applications sorted by their sort order and provides
 * navigation capabilities.
 *
 * @returns {JSX.Element} The rendered Discovery component
 */
function Discovery(): JSX.Element {
  const history = useHistory();
  const formatMessage = useLocalization();
  const {
    data: discoveryData,
    isLoading,
    isError,
    refetch,
  } = useGetDiscoveryApplicationsQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });
  const handleBackToHomePage = useCallback(() => {
    history.push(config.URL_OB_NEXT_GEN);
  }, [history]);

  const isDocLinkAvailable = useMemo(
    () => Boolean(discoveryData?.docLink),
    [discoveryData]
  );
  const sortedDiscoveryApps =
    discoveryData &&
    [...discoveryData.list].sort((a, b) => {
      const sortOrderA = a.applications[0]?.sortOrder ?? 0;
      const sortOrderB = b.applications[0]?.sortOrder ?? 0;

      return sortOrderA - sortOrderB;
    });

  const renderedCards = useMemo(
    () =>
      sortedDiscoveryApps?.map(({ applications }) => {
        const app = applications[0];

        return (
          <ApplicationCard
            key={app.id}
            appTypeKey={app.id}
            image={app.image}
            name={app.name}
            title={app.displayName}
            description={app.description}
            applicationGroup={app.applicationGroup}
            connectionType={app.connectionType}
            status={app.status}
          />
        );
      }),
    [sortedDiscoveryApps]
  );

  const handleReload = async () => {
    try {
      await refetch();
    } catch (err) {
      Logger.error('Failed to reload:', err);
    }
  };

  return (
    <OverlayProvider>
      <div className='Discovery'>
        <Box className='Discovery-Back'>
          <Button
            type='button'
            kind='ghost'
            size='large'
            leftIcon={<Icon kind='ChevronLeft' />}
            onClick={handleBackToHomePage}>
            {formatMessage('INTEGRATIONS.DISCOVERY.BACK', 'Back')}
          </Button>
        </Box>

        <section className='Discovery-Container' aria-label='Discovery Content'>
          {isError ? (
            <IntegrationPageError
              illustrationKind='Error'
              heading='INTEGRATIONS.DISCOVERY.ERROR.HEADING'
              subHeading='INTEGRATIONS.DISCOVERY.ERROR.SUBHEADING'
              reload={handleReload}
            />
          ) : (
            <>
              <div
                role='heading'
                aria-level={1}
                className='Discovery-Container-Title'>
                {formatMessage(
                  'INTEGRATIONS.DISCOVERY.TITLE',
                  'Setup Discovery'
                )}
              </div>
              <div className='Discovery-Container-description'>
                <Typography kind='body2'>
                  {formatMessage(
                    'INTEGRATIONS.DISCOVERY.DESCRIPTION',
                    'Discover your apps and workloads through one of these popular discovery sources. You will unlock benefits like prefilled details, prioritized recommendations, and a full view of onboarded status.'
                  )}
                  {isDocLinkAvailable && (
                    <DsLink
                      className='Discovery-Container-description-link'
                      text={formatMessage(
                        'INTEGRATIONS.DISCOVERY.LEARNMORE',
                        'Learn More'
                      )}
                      size='medium'
                      suffixIconKind='JumpLink'
                      href={discoveryData?.docLink ? discoveryData.docLink : ''}
                    />
                  )}
                </Typography>
              </div>

              {isLoading ? (
                <section className='Discovery-Container-Loader'>
                  <Loader color='brand' format='main' kind='loop' />
                </section>
              ) : (
                <section className='Discovery-Container-applications'>
                  {renderedCards}
                </section>
              )}
              <DiscoverCMDBFooter />
            </>
          )}
        </section>
      </div>
    </OverlayProvider>
  );
}

export default memo(Discovery);
